import { Grid, InputBase } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import FormInput from 'components/common/FormInput'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import DazzedParagraph16 from 'components/common/text/DazzedParagraph16'
import { PatientBriefCategoryOrder } from 'consts/hiToolsConsts'
import _ from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isMobile } from 'utils/mobileUtils'
import PatientBriefCategory from './PatientBriefCategory'

const useStyles = makeStyles(theme => ({
  customNameInput: {
    width: '250px',
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  practiceNotesInput: {
    width: '457px',
    fontSize: 16,
    color: 'var(--text-color-6)',
    borderBottom: '1px solid var(--border-color-13)',
    // The desktop's 457px is wider than a phone's viewport — let the input fill the
    // available width inside the (now full-screen) mobile modal.
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  },
  practiceNoteText: {
    color: 'var(--text-color-11)'
  }
}))

const PatientBriefForm = ({ values, setValues, errors = {}, viewMode = false }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  // The desktop layout puts categories in two side-by-side columns (~940px wide). That
  const isCompactLayout = isMobile()

  const handlePracticeNotesKeyDown = useCallback(e => {
    if (e.key === 'Escape') {
      e.target.blur()
    }
  }, [])

  const categoryColumns = useMemo(() => {
    const categories = _.sortBy(Object.keys(values?.categories || {}), category => PatientBriefCategoryOrder[category])
    if (!categories.length) {
      return []
    }

    if (isCompactLayout) {
      return [categories]
    }

    const middle = Math.ceil(categories.length / 2)
    const firstHalf = categories.slice(0, middle)
    const secondHalf = categories.slice(middle)
    return [firstHalf, secondHalf]
  }, [values, isCompactLayout])

  return (
    <Grid container direction="column" style={{ height: '100%' }} justifyContent="space-between">
      <Grid item>
        <Grid container direction="column">
          <Grid item style={{ marginBottom: 32 }}>
            {viewMode ? (
              <DazzedParagraph16 bold>{values.customTitle}</DazzedParagraph16>
            ) : (
              <FormInput
                value={values.customTitle}
                setValue={value => setValues({ ...values, customTitle: value })}
                style={{ bright: true, thick: true, borderBottom: true }}
                placeholder={t('dialogs.patientBrief.nameInputPlaceholder')}
                containerClassName={classes.customNameInput}
              />
            )}
          </Grid>
          <Grid item>
            <Grid container justifyContent="space-between">
              {categoryColumns.map((categories, rowIndex) => (
                <Grid item key={`column-${rowIndex}`} xs={isCompactLayout ? 12 : undefined}>
                  <Grid container direction="column">
                    {categories.map(category => (
                      <Grid item key={`patient-brief-category-${category}`} style={{ marginBottom: 16 }}>
                        <PatientBriefCategory
                          category={category}
                          categoryValues={values.categories[category]}
                          setCategoryValues={categoryValues =>
                            setValues({ ...values, categories: { ...values.categories, [category]: categoryValues } })
                          }
                          error={errors?.categories?.[category]}
                          viewMode={viewMode}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item style={{ marginBottom: 16 }}>
        {viewMode ? (
          <DazzedParagraph14 className={classes.practiceNoteText} style={{ whiteSpace: 'pre-line' }}>
            <span style={{ fontWeight: 500, marginRight: 5 }}>{t('dialogs.patientBrief.generalNoteLabel')} </span>
            {values.practiceNotes ? <span>{values.practiceNotes}</span> : <span style={{ fontWeight: 500 }}>-</span>}
          </DazzedParagraph14>
        ) : (
          <InputBase
            value={values.practiceNotes}
            onChange={e => setValues({ ...values, practiceNotes: e.target.value })}
            placeholder={t('dialogs.patientBrief.practiceNotesPlaceholder')}
            className={classes.practiceNotesInput}
            multiline
            onKeyDown={handlePracticeNotesKeyDown}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default PatientBriefForm
